import React from "react";
import { TIER_MONTHS } from "@/lib/pricing";
import JeaToggle from "@/components/jea/JeaToggle";
import JeaButton from "@/components/jea/JeaButton";
import { shortDate } from "@/lib/dates";
import { getThemeAccent } from "@/theme/tokens";
import { cn } from "@/lib/utils";

const CIRCLE_SIZE = 96;
const RADIUS = 42;
const CIRC = 2 * Math.PI * RADIUS;

const daysForTier = (tier) => (TIER_MONTHS[tier] || 12) * 30;

const CircularCountdown = ({ daysUntil, tier }) => {
  const total = daysForTier(tier);
  const remaining = Math.max(0, Math.min(total, daysUntil ?? 0));
  const pct = total > 0 ? remaining / total : 0;
  const dash = CIRC * pct;
  return (
    <div className="relative flex items-center justify-center" style={{ width: CIRCLE_SIZE, height: CIRCLE_SIZE }}>
      <svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} className="-rotate-90">
        <circle cx={CIRCLE_SIZE / 2} cy={CIRCLE_SIZE / 2} r={RADIUS} stroke="#1E3A58" strokeWidth={6} fill="none" />
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={RADIUS}
          stroke={getThemeAccent()}
          strokeWidth={6}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${CIRC - dash}`}
          data-testid="query-countdown-arc"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="text-accent-pink font-bold text-[20px] leading-none" data-testid="query-countdown-days">
            {daysUntil ?? 0}
          </div>
          <div className="text-[10px] tracking-[0.06em] uppercase text-text-secondary mt-1">days</div>
        </div>
      </div>
    </div>
  );
};

const QueryScheduleCard = ({ debtor, onSkip, onRunNow }) => {
  const daysUntil = debtor?.critical_dates?.dates?.next_query?.days_until;
  const iso = debtor?.critical_dates?.dates?.next_query?.iso || debtor?.next_query_at;
  const [skipping, setSkipping] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  const toggleSkip = () => setConfirming(true);
  const confirmSkip = async () => {
    setSkipping(true);
    try {
      await onSkip();
    } finally {
      setConfirming(false);
      setSkipping(false);
    }
  };

  return (
    <div className="bg-bg-secondary border border-border-default rounded-[12px] p-6" data-testid="query-schedule-card">
      <h3 className="text-white font-semibold text-[15px]">Next Scheduled Query</h3>
      <div className="mt-4 flex flex-col items-center gap-3">
        <CircularCountdown daysUntil={daysUntil} tier={debtor.tier} />
        <div className="jea-date text-center text-[13px]" data-testid="query-schedule-date">
          {shortDate(iso)}
        </div>
      </div>
      {onRunNow && (
        <JeaButton fullWidth className="mt-5" onClick={onRunNow} data-testid="query-run-now">
          Run Query Now
        </JeaButton>
      )}
      {onSkip && (
        <div className={cn("flex items-center justify-between text-[13px] text-text-secondary", onRunNow ? "mt-3" : "mt-5")}>
          <span>Skip Next Scheduled Query</span>
          <JeaToggle checked={false} onChange={toggleSkip} data-testid="query-skip-toggle" />
        </div>
      )}

      {confirming && (
        <div className="mt-3 bg-bg-tertiary border border-warning/40 rounded-[8px] p-3 text-[13px] space-y-3" data-testid="query-skip-confirm">
          <div className="text-white">
            Skip the next automated query for <span className="font-semibold text-accent-pink">{debtor.full_name}</span>?
            The following query will still run on schedule.
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="h-9 px-3 rounded-[8px] border border-border-default text-text-secondary text-[13px] hover:text-white hover:bg-bg-primary transition-colors"
              data-testid="query-skip-cancel"
            >
              Cancel
            </button>
            <JeaButton size="sm" onClick={confirmSkip} loading={skipping} data-testid="query-skip-confirm-cta">
              Confirm
            </JeaButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryScheduleCard;
